/**
 * i18n — Internationalization framework
 * Top 10 languages: English, Spanish, French, Arabic, Hindi, Japanese, Chinese, Korean, Portuguese, German
 */
import { createContext, useContext, useState, useCallback } from 'react';

const LANGUAGES = {
  en: { name: 'English', dir: 'ltr', flag: 'GB' },
  es: { name: 'Espa\u00f1ol', dir: 'ltr', flag: 'ES' },
  fr: { name: 'Fran\u00e7ais', dir: 'ltr', flag: 'FR' },
  ar: { name: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', dir: 'rtl', flag: 'SA' },
  hi: { name: '\u0939\u093f\u0928\u094d\u0926\u0940', dir: 'ltr', flag: 'IN' },
  ja: { name: '\u65e5\u672c\u8a9e', dir: 'ltr', flag: 'JP' },
  zh: { name: '\u4e2d\u6587', dir: 'ltr', flag: 'CN' },
  ko: { name: '\ud55c\uad6d\uc5b4', dir: 'ltr', flag: 'KR' },
  pt: { name: 'Portugu\u00eas', dir: 'ltr', flag: 'BR' },
  de: { name: 'Deutsch', dir: 'ltr', flag: 'DE' },
};

// Core UI translations (expandable)
const translations = {
  en: {
    feed: 'Feed', post: 'Post', ai_search: 'AI Search', profile: 'Profile',
    discover: 'Discover', promote: 'Promote', sign_in: 'Sign In', sign_out: 'Sign Out',
    foodie_feed: 'Foodie Feed', near_you: 'Near You', deals: 'Deals',
    ai_quests: 'AI Daily Quests', events: 'Events', hidden_gems: 'Hidden Gems',
    see_all: 'See All', search: 'Search', messages: 'Messages', communities: 'Communities',
    challenges: 'Challenges', spaces: 'Live Spaces', settings: 'Settings',
    foodlist: 'FoodList', foodcard: 'FoodCard', reviews: 'Reviews', share: 'Share',
    verified: 'Verified', unverified: 'Unverified', promoted: 'Promoted',
    your_recipe: 'Your Recipe', post_foodcard: 'Post FoodCard',
  },
  es: {
    feed: 'Inicio', post: 'Publicar', ai_search: 'B\u00fasqueda IA', profile: 'Perfil',
    discover: 'Descubrir', promote: 'Promover', sign_in: 'Iniciar Sesi\u00f3n', sign_out: 'Cerrar Sesi\u00f3n',
    foodie_feed: 'Feed Foodie', near_you: 'Cerca de Ti', deals: 'Ofertas',
    ai_quests: 'Desaf\u00edos IA', events: 'Eventos', hidden_gems: 'Joyas Ocultas',
    see_all: 'Ver Todo', search: 'Buscar', messages: 'Mensajes', communities: 'Comunidades',
    challenges: 'Desaf\u00edos', spaces: 'Espacios en Vivo', settings: 'Configuraci\u00f3n',
    foodlist: 'FoodList', foodcard: 'FoodCard', reviews: 'Rese\u00f1as', share: 'Compartir',
    verified: 'Verificado', unverified: 'No Verificado', promoted: 'Promovido',
    your_recipe: 'Tu Receta', post_foodcard: 'Publicar FoodCard',
  },
  fr: {
    feed: 'Accueil', post: 'Publier', ai_search: 'Recherche IA', profile: 'Profil',
    discover: 'D\u00e9couvrir', promote: 'Promouvoir', sign_in: 'Se Connecter', sign_out: 'D\u00e9connexion',
    foodie_feed: 'Fil Foodie', near_you: 'Pr\u00e8s de Vous', deals: 'Offres',
    ai_quests: 'D\u00e9fis IA', events: '\u00c9v\u00e9nements', hidden_gems: 'P\u00e9pites Cach\u00e9es',
    see_all: 'Tout Voir', search: 'Rechercher', messages: 'Messages', communities: 'Communaut\u00e9s',
    challenges: 'D\u00e9fis', spaces: 'Espaces Live', settings: 'Param\u00e8tres',
    foodlist: 'FoodList', foodcard: 'FoodCard', reviews: 'Avis', share: 'Partager',
    verified: 'V\u00e9rifi\u00e9', unverified: 'Non V\u00e9rifi\u00e9', promoted: 'Promu',
    your_recipe: 'Votre Recette', post_foodcard: 'Publier FoodCard',
  },
  ar: {
    feed: '\u0627\u0644\u0631\u0626\u064a\u0633\u064a\u0629', post: '\u0646\u0634\u0631', ai_search: '\u0628\u062d\u062b \u0630\u0643\u064a', profile: '\u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0634\u062e\u0635\u064a',
    discover: '\u0627\u0643\u062a\u0634\u0641', promote: '\u062a\u0631\u0648\u064a\u062c', sign_in: '\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644', sign_out: '\u062e\u0631\u0648\u062c',
    see_all: '\u0639\u0631\u0636 \u0627\u0644\u0643\u0644', search: '\u0628\u062d\u062b',
    foodlist: 'FoodList', foodcard: 'FoodCard', reviews: '\u062a\u0642\u064a\u064a\u0645\u0627\u062a', share: '\u0645\u0634\u0627\u0631\u0643\u0629',
  },
  hi: {
    feed: '\u092b\u0940\u0921', post: '\u092a\u094b\u0938\u094d\u091f', ai_search: 'AI \u0916\u094b\u091c', profile: '\u092a\u094d\u0930\u094b\u092b\u093e\u0907\u0932',
    discover: '\u0916\u094b\u091c\u0947\u0902', promote: '\u092a\u094d\u0930\u094b\u092e\u094b\u091f', sign_in: '\u0938\u093e\u0907\u0928 \u0907\u0928', sign_out: '\u0938\u093e\u0907\u0928 \u0906\u0909\u091f',
    see_all: '\u0938\u092c \u0926\u0947\u0916\u0947\u0902', search: '\u0916\u094b\u091c\u0947\u0902',
  },
  ja: {
    feed: '\u30d5\u30a3\u30fc\u30c9', post: '\u6295\u7a3f', ai_search: 'AI\u691c\u7d22', profile: '\u30d7\u30ed\u30d5\u30a3\u30fc\u30eb',
    discover: '\u767a\u898b', promote: '\u30d7\u30ed\u30e2\u30fc\u30c8', sign_in: '\u30b5\u30a4\u30f3\u30a4\u30f3', sign_out: '\u30b5\u30a4\u30f3\u30a2\u30a6\u30c8',
    see_all: '\u3059\u3079\u3066\u898b\u308b', search: '\u691c\u7d22',
  },
  zh: {
    feed: '\u52a8\u6001', post: '\u53d1\u5e03', ai_search: 'AI\u641c\u7d22', profile: '\u4e2a\u4eba\u8d44\u6599',
    discover: '\u53d1\u73b0', promote: '\u63a8\u5e7f', sign_in: '\u767b\u5f55', sign_out: '\u9000\u51fa',
    see_all: '\u67e5\u770b\u5168\u90e8', search: '\u641c\u7d22',
  },
  ko: {
    feed: '\ud53c\ub4dc', post: '\uac8c\uc2dc', ai_search: 'AI \uac80\uc0c9', profile: '\ud504\ub85c\ud544',
    discover: '\ubc1c\uacac', promote: '\ud64d\ubcf4', sign_in: '\ub85c\uadf8\uc778', sign_out: '\ub85c\uadf8\uc544\uc6c3',
    see_all: '\ubaa8\ub450 \ubcf4\uae30', search: '\uac80\uc0c9',
  },
  pt: {
    feed: 'In\u00edcio', post: 'Publicar', ai_search: 'Busca IA', profile: 'Perfil',
    discover: 'Descobrir', promote: 'Promover', sign_in: 'Entrar', sign_out: 'Sair',
    see_all: 'Ver Tudo', search: 'Buscar',
  },
  de: {
    feed: 'Feed', post: 'Posten', ai_search: 'KI-Suche', profile: 'Profil',
    discover: 'Entdecken', promote: 'Bewerben', sign_in: 'Anmelden', sign_out: 'Abmelden',
    see_all: 'Alle Anzeigen', search: 'Suchen',
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('fq_lang') || 'en');

  const changeLang = useCallback((newLang) => {
    if (LANGUAGES[newLang]) {
      setLang(newLang);
      localStorage.setItem('fq_lang', newLang);
      document.documentElement.dir = LANGUAGES[newLang].dir;
    }
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations.en?.[key] || key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, changeLang, t, languages: LANGUAGES, dir: LANGUAGES[lang]?.dir || 'ltr' }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be inside I18nProvider');
  return ctx;
};

export { LANGUAGES };
